import { Entity, Property } from '@square/ignition';
import {
  ApiExplorerInputType,
  ApiExplorerMapFieldValue,
  ApiExplorerObjectInput,
} from '@squareup/dex-types-shared-api-explorer';
import { isObject } from '@squareup/dex-utils-shared-json';

const isApiExplorerMapFieldValue = (
  value: unknown
): value is ApiExplorerMapFieldValue => {
  if (!value || !Array.isArray(value)) {
    return false;
  }

  if (value.length === 0) {
    return true;
  }

  return value.every((item) => {
    if (
      Array.isArray(item) &&
      item.length === 2 &&
      typeof item[0] === 'string'
    ) {
      return true;
    }

    return false;
  });
};

/**
 * Converts a given input object that may contain Map state within the API Explorer
 * into a JSON object. Specifically, our API Explorer treats Map field input
 * as a tupled array, such as Array<[string, unknown]>. This function will convert any occurrences
 * into a JSON object, as Record<string, unknown>. This is necessary to send data to the APIs or the
 * code generators.
 * The assumption is the incoming property is a map, and we will process the input accordingly.
 * Afterwards, we will call the walkFn to continue walking the input.
 * @returns A cloned JSON object where all Map field arrays are converted to JSON objects.
 */
const convertApiExplorerMapInputToJsonMap = (
  property: Property,
  inputVal: ApiExplorerInputType,
  walkFn: (
    input: ApiExplorerObjectInput,
    entity?: Entity
  ) => ApiExplorerInputType
): Record<string, unknown> => {
  const newMap: Record<string, unknown> = {};

  if (Array.isArray(inputVal)) {
    for (const pair of inputVal) {
      newMap[pair[0]] = pair[1];
    }
  }

  if (property.containsEntity) {
    for (const mapKey of Object.keys(newMap)) {
      // eslint-disable-next-line no-use-before-define
      newMap[mapKey] = walkFn(
        newMap[mapKey] as ApiExplorerObjectInput,
        property.itemEntity
      );
    }
  }

  return newMap;
};

const convertJsonMapInputToApiExplorerMapInput = (
  input: ApiExplorerObjectInput,
  entity?: Entity
): ApiExplorerObjectInput => {
  if (!entity || !input) {
    return input;
  }

  const clonedInput = structuredClone(input);

  const newNode: ApiExplorerObjectInput = {};
  for (const inputKey of Object.keys(clonedInput)) {
    const property = entity.propertyMap.get(inputKey);
    if (!property) {
      continue;
    }

    if (property.isMap) {
      const newMap: ApiExplorerMapFieldValue = [];

      // This should be a JSON object
      const inputVal = clonedInput[inputKey];

      // This particular map input is already the right type. No need to convert, but need
      // to check the values
      if (isApiExplorerMapFieldValue(inputVal)) {
        for (const [entryKey, entryVal] of inputVal) {
          if (property.containsEntity) {
            newMap.push([
              entryKey,
              convertJsonMapInputToApiExplorerMapInput(
                entryVal as ApiExplorerObjectInput,
                property.itemEntity
              ),
            ]);
          } else {
            newMap.push([entryKey, entryVal as ApiExplorerInputType]);
          }
        }
      } else if (isObject(inputVal)) {
        for (const [entryKey, entryVal] of Object.entries(inputVal)) {
          if (property.containsEntity) {
            newMap.push([
              entryKey,
              convertJsonMapInputToApiExplorerMapInput(
                entryVal as ApiExplorerObjectInput,
                property.itemEntity
              ),
            ]);
          } else {
            newMap.push([entryKey, entryVal as ApiExplorerInputType]);
          }
        }
      }

      newNode[inputKey] = newMap;
    } else if (property.isArray) {
      if (property.containsEntity) {
        const newArray = [];
        const inputVal = clonedInput[inputKey];
        if (Array.isArray(inputVal)) {
          for (const item of inputVal) {
            newArray.push(
              convertJsonMapInputToApiExplorerMapInput(
                item,
                property.itemEntity
              )
            );
          }
          newNode[inputKey] = newArray;
        }
      } else {
        newNode[inputKey] = clonedInput[inputKey];
      }
    } else if (property.isAny || property.isFreeformObject) {
      // Special case, return if the value is Any type or
      // a freeform object
      return clonedInput;
    } else if (property.isEntity) {
      newNode[inputKey] = convertJsonMapInputToApiExplorerMapInput(
        clonedInput[inputKey] as ApiExplorerObjectInput,
        property.entity
      );
    } else {
      newNode[inputKey] = clonedInput[inputKey];
    }
  }

  return newNode;
};

export {
  convertApiExplorerMapInputToJsonMap,
  convertJsonMapInputToApiExplorerMapInput,
  isApiExplorerMapFieldValue,
};
